import { CenteredLazyLoading } from "@/controls/LazyLoading";
import InsightsIcon from "@mui/icons-material/Insights";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import type { InspectionDashboard } from "../../models/InspectionDashboard";
import { getInspectionDashboard } from "../../repositories/InspectionReportRepository";
import { DashboardCard } from "./dashboard/DashboardCard";
import { DashboardTiles } from "./dashboard/DashboardTiles";
import { DefectMixChart } from "./dashboard/DefectMixChart";
import { BUCKET_NAMES, FindingRateChart } from "./dashboard/FindingRateChart";
import {
	toRankedHeats,
	toRankedSections,
	WorstGroupsChart,
} from "./dashboard/WorstGroupsChart";

export const useInspectionDashboard = ({
	period,
	eventId,
	enabled = true,
}: {
	period: { startDate: Date; endDate: Date } | null;
	eventId: number | null;
	enabled?: boolean;
}) =>
	useQuery<InspectionDashboard, Error>({
		queryKey: [
			"inspection-dashboard",
			eventId === null ? (period?.startDate.getTime() ?? null) : null,
			eventId === null ? (period?.endDate.getTime() ?? null) : null,
			eventId,
		],
		queryFn: ({ signal }) =>
			getInspectionDashboard(
				{
					startDate: eventId === null ? (period?.startDate ?? null) : null,
					endDate: eventId === null ? (period?.endDate ?? null) : null,
					eventId,
				},
				signal,
			),
		enabled,
	});

type Props = {
	period: { startDate: Date; endDate: Date } | null;
	eventId: number | null;
	isLoadingEvents?: boolean;
	periodLabel: string;
	onError: (message: string) => void;
	onSectionSelect?: (sectionId: number, sectionName: string) => void;
	canSelectSection?: (sectionId: number) => boolean;
};

const EmptyState = ({ message }: { message: string }) => (
	<Box sx={{ color: "text.secondary", py: 6, textAlign: "center" }}>
		<InsightsIcon sx={{ fontSize: 40, opacity: 0.35 }} />
		<Typography variant="body2" mt={1}>
			{message}
		</Typography>
	</Box>
);

const CenteredCard = ({ children }: { children: ReactNode }) => (
	<Paper
		elevation={0}
		sx={{
			alignItems: "center",
			border: 1,
			borderColor: "divider",
			borderRadius: 1,
			display: "flex",
			height: "77vh",
			justifyContent: "center",
		}}
	>
		{children}
	</Paper>
);

export const InspectionsReportDashboard = ({
	period,
	eventId,
	isLoadingEvents = false,
	periodLabel,
	onError,
	onSectionSelect,
	canSelectSection = () => true,
}: Props) => {
	const isEvent = eventId !== null;
	const isReady = isEvent || period !== null;

	const { data, isLoading, isError, error } = useInspectionDashboard({
		period,
		eventId,
		enabled: isReady,
	});

	useEffect(() => {
		if (isError && error) {
			onError(error.message);
		}
	}, [isError, error, onError]);

	if (!isReady)
		return (
			<CenteredCard>
				{isLoadingEvents ? (
					<CenteredLazyLoading />
				) : (
					<EmptyState message="Choose a date range or a production event." />
				)}
			</CenteredCard>
		);

	if (isLoading || !data)
		return (
			<CenteredCard>
				<CenteredLazyLoading />
			</CenteredCard>
		);

	const { grain, totals, buckets, defects, sections, heats } = data;

	const worst = isEvent ? toRankedHeats(heats) : toRankedSections(sections);

	return (
		<Box sx={{ height: "73vh", overflowY: "auto", pr: 0.5 }}>
			<Grid2 container spacing={1}>
				<Grid2 size={12}>
					<DashboardTiles totals={totals} />
				</Grid2>
				<Grid2 size={12}>
					<DashboardCard
						title="Share of readings with a finding"
						subtitle={`${periodLabel} · ${BUCKET_NAMES[grain]} · % of that bucket's readings`}
					>
						<FindingRateChart
							buckets={buckets}
							grain={grain}
							isEvent={isEvent}
							periodLabel={periodLabel}
						/>
					</DashboardCard>
				</Grid2>
				<Grid2 size={{ xs: 12, lg: 7 }}>
					<DashboardCard
						title="What kind of defect"
						subtitle={`${periodLabel} · ${totals.Defects} tagged`}
						footer={
							defects.length === 0
								? undefined
								: "% of all defects tagged · times tagged"
						}
					>
						{defects.length === 0 ? (
							<EmptyState message="No surface defects were tagged." />
						) : (
							<DefectMixChart
								defects={defects}
								trend={data.defectTrend}
								grain={grain}
								isEvent={isEvent}
							/>
						)}
					</DashboardCard>
				</Grid2>
				<Grid2 size={{ xs: 12, lg: 5 }}>
					<DashboardCard
						title={isEvent ? "Worst heats" : "Worst sections"}
						subtitle={`${periodLabel} · top ${Math.min(worst.length, 5)}`}
						footer={
							worst.length === 0
								? undefined
								: `% of go/no-go checks that failed per ${isEvent ? "heat" : "section"} · failed checks`
						}
					>
						{worst.length === 0 ? (
							<EmptyState message="No go/no-go checks were recorded." />
						) : (
							<WorstGroupsChart
								groups={worst}
								onSelect={
									isEvent || !onSectionSelect
										? undefined
										: (group) => onSectionSelect(group.Id, group.Name)
								}
								canSelect={(group) => canSelectSection(group.Id)}
							/>
						)}
					</DashboardCard>
				</Grid2>
			</Grid2>
		</Box>
	);
};
